import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { hrAPI } from '../../services/api';
import { AuthImg, deptLabel } from './hrShared';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';
const errText = (e, fb) => (e && e.response && e.response.data && e.response.data.error) || fb;
// V71 — fotoğraf token'lı çekilir (düz URL 401 alıyordu)
const selfPhotoPath = (fotoPath, bust) => (fotoPath ? `/hr/self/photo/${fotoPath.split('/').pop()}${bust ? `?t=${bust}` : ''}` : null);
const initialsOf = (name) => (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

export default function HRProfile() {
  const [profile, setProfile] = useState(null);
  const [editable, setEditable] = useState([]);
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [revealBusy, setRevealBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [photoBust, setPhotoBust] = useState(0);
  const fileRef = useRef(null);

  // V71 — `silent`: kart sökülmeden yenilenir (hassas bilgi göster/gizle sayfayı yanıp söndürmesin)
  const load = useCallback(async (rev = false, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await hrAPI.selfProfile(rev);
      setProfile(r.data?.data);
      setEditable(r.data?.editable_fields || []);
      setLoadErr(null);
      return true;
    } catch (e) {
      if (silent) toast.error(errText(e, 'Profil yenilenemedi.'));
      else setLoadErr(errText(e, 'Profil yüklenemedi.'));
      return false;
    } finally { if (!silent) setLoading(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);

  const startEdit = () => {
    const f = {};
    editable.forEach(k => { f[k] = profile?.[k] || ''; });
    setForm(f);
    setEditMode(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await hrAPI.selfProfileUpdate(form);
      setEditMode(false);
      await load(reveal, true);
      toast.success('Bilgileriniz kaydedildi');
    } catch (e) { toast.error(errText(e, 'Kayıt hatası')); }
    setSaving(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    try {
      await hrAPI.selfPhotoUpload(file);
      setPhotoBust(Date.now());
      await load(reveal, true);
      toast.success('Fotoğraf güncellendi');
    } catch (e) { toast.error(errText(e, 'Fotoğraf yüklenemedi')); }
  };

  const toggleReveal = async () => {
    const next = !reveal;
    setRevealBusy(true);
    if (await load(next, true)) setReveal(next);
    setRevealBusy(false);
  };

  if (loading) return <div className="loading-state">Yükleniyor…</div>;
  if (loadErr) {
    return (
      <div className="error-state">
        ⚠️ {loadErr}
        <button className="btn btn-secondary btn-sm" onClick={() => load(reveal)}>Tekrar dene</button>
      </div>
    );
  }
  if (!profile) return <div className="hr-card hr-empty-card"><h3>Profil bulunamadı.</h3></div>;

  const photoPath = selfPhotoPath(profile.foto_path, photoBust);
  const initials = initialsOf(profile.name);

  const isMaskValue = (v) => typeof v === 'string' && /^\*+/.test(v);

  return (
    <div>
      <div className="hr-card">
        <div className="hr-profile-grid">
          <div className="hr-photo-wrap">
            <AuthImg path={photoPath} className="hr-photo" alt={profile.name}
              fallback={<div className="hr-photo">{initials}</div>} />
            <button className="btn btn-secondary btn-xs" onClick={() => fileRef.current?.click()}>
              📷 Fotoğraf Değiştir
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => uploadPhoto(e.target.files?.[0])} />
            <div className="hr-photo-name">
              <strong>{profile.name}</strong>
              <span>{profile.unvan || '—'}</span>
              <small>{deptLabel(profile) || '—'} · {profile.lokasyon || '—'}</small>
            </div>
          </div>

          <div>
            <div className="hr-card-head-row">
              <h3 className="hr-card-title">👤 Kişisel Bilgilerim</h3>
              <div className="hr-btn-row">
                <button className={`btn btn-xs ${reveal ? 'btn-warning' : 'btn-secondary'}`} onClick={toggleReveal} disabled={revealBusy}>
                  {revealBusy ? <span className="spinner sm" /> : null}
                  {reveal ? '🔓 Hassas Bilgileri Gizle' : '🔒 Hassas Bilgileri Göster'}
                </button>
                {!editMode && <button className="btn btn-primary btn-grad btn-xs" onClick={startEdit}>✏️ Düzenle</button>}
              </div>
            </div>

            {!editMode ? (
              <div className="hr-info-grid">
                <Info label="Sicil No" value={profile.sicil_no} />
                <Info label="Pozisyon" value={profile.pozisyon} />
                <Info label="İşe Giriş" value={fmtDate(profile.ise_giris_tarihi)} />
                <Info label="Doğum Tarihi" value={fmtDate(profile.dogum_tarihi)} />
                <Info label="TC Kimlik No" value={profile.tc_kimlik} mask={isMaskValue(profile.tc_kimlik)} />
                <Info label="Pasaport No" value={profile.pasaport_no} mask={isMaskValue(profile.pasaport_no)} />
                <Info label="Pasaport Bitiş" value={fmtDate(profile.pasaport_gecerlilik)} />
                <Info label="SGK No" value={profile.sgk_no} mask={isMaskValue(profile.sgk_no)} />
                <Info label="IBAN" value={profile.iban} mask={isMaskValue(profile.iban)} />
                <Info label="Telefon" value={profile.iletisim_tel} />
                <Info label="E-posta" value={profile.iletisim_email} />
                <Info label="Adres" value={profile.adres} wide />
                <Info label="Acil Durum Kişi" value={profile.acil_durum_kisi} />
                <Info label="Acil Durum Tel" value={profile.acil_durum_tel} />
                <Info label="Medeni Hal" value={profile.medeni_hal} />
                <Info label="Çocuk Sayısı" value={profile.cocuk_sayisi} />
                <Info label="Eğitim Durumu" value={profile.egitim_durumu} />
              </div>
            ) : (
              <div>
                <p className="hr-form-note">
                  Sadece iletişim ve acil durum bilgilerini düzenleyebilirsin. TC, pasaport, SGK, IBAN gibi bilgileri İK ekibi günceller.
                </p>
                <div className="hr-info-grid">
                  <FormField label="Telefon" name="iletisim_tel" form={form} setForm={setForm} />
                  <FormField label="E-posta" name="iletisim_email" form={form} setForm={setForm} type="email" />
                  <FormField label="Adres" name="adres" form={form} setForm={setForm} wide />
                  <FormField label="Acil Durum Kişi" name="acil_durum_kisi" form={form} setForm={setForm} />
                  <FormField label="Acil Durum Tel" name="acil_durum_tel" form={form} setForm={setForm} />
                  <FormField label="Medeni Hal" name="medeni_hal" form={form} setForm={setForm} options={['Bekar', 'Evli', 'Boşanmış', 'Dul']} />
                  <FormField label="Çocuk Sayısı" name="cocuk_sayisi" form={form} setForm={setForm} type="number" />
                  <FormField label="Eğitim Durumu" name="egitim_durumu" form={form} setForm={setForm} options={['İlkokul', 'Ortaokul', 'Lise', 'Önlisans', 'Lisans', 'Yüksek Lisans', 'Doktora']} />
                </div>
                <div className="hr-form-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(false)}>İptal</button>
                  <button className="btn btn-primary btn-grad btn-sm" onClick={save} disabled={saving}>
                    {saving ? 'Kaydediliyor…' : 'Kaydet'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {profile.manager && (
        <div className="hr-card">
          <h3 className="hr-card-title">⬆️ Yöneticim</h3>
          <div className="hr-person">
            <AuthImg path={selfPhotoPath(profile.manager.foto_path)} className="hr-person-ava" alt=""
              fallback={<div className="hr-person-ava">{initialsOf(profile.manager.name)}</div>} />
            <div className="hr-person-meta">
              <div className="hr-person-name">{profile.manager.name}</div>
              <div className="hr-person-sub">{profile.manager.unvan || profile.manager.pozisyon || '—'}</div>
              {profile.manager.iletisim_email && <div className="hr-person-mail">{profile.manager.iletisim_email}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, wide, mask }) {
  return (
    <div className={`hr-info-item${wide ? ' wide' : ''}`}>
      <div className="hr-info-label">{label}</div>
      <div className={`hr-info-value ${mask ? 'hr-info-value-mask' : ''}`}>
        {value != null && value !== '' ? value : '—'}
      </div>
    </div>
  );
}

function FormField({ label, name, form, setForm, type = 'text', options, wide }) {
  return (
    <div className={`hr-info-item${wide ? ' wide' : ''}`}>
      <label className="hr-info-label" htmlFor={name}>{label}</label>
      {options ? (
        <select id={name} className="form-select" value={form[name] || ''} onChange={e => setForm({ ...form, [name]: e.target.value })}>
          <option value="">Seçiniz</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input id={name} className="form-input" type={type} value={form[name] || ''} onChange={e => setForm({ ...form, [name]: e.target.value })} />
      )}
    </div>
  );
}
